'use client'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { getProductOriginalPrice, getProductPrice, notEmpty } from 'shared-utils/utils'
import type { OldProductDTO, Option, ProductPrice, UIVariant } from 'ecosystem'

export interface UseConfigurationVariantsHook {
  prices: ProductPrice
  selectedConfigurations: UIVariant[]
  selectConfiguration: (variant: UIVariant) => void
  isConfigurationSelected: (variant: UIVariant) => boolean
  selectConfigurationOnUpdate: (variantGroupId: string, variants: Option<UIVariant[]>) => void
  isConfigurationBuyable: boolean
}

interface PriceReducerAction {
  type: 'sum' | 'set'
  payload: ProductPrice
}

const priceReducer = (state: ProductPrice, action: PriceReducerAction) => {
  switch (action.type) {
    case 'set':
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const useConfigurationVariants = (
  product: OldProductDTO,
  variantsByIds?: Option<Record<string, { data?: OldProductDTO[] | null; error?: string }>>
): UseConfigurationVariantsHook => {
  const [prices, dispatchPrices] = useReducer(priceReducer, {
    price: product.price,
    originalPrice: product.originalPrice
  })
  const [selectedConfigurations, setSelectedConfigurations] = useState<UIVariant[]>([])

  const requiredGroupsIds = useMemo(() => {
    return (
      product.configurations
        ?.filter((e) => !e.optional)
        .map((e) => e.variantGroupId)
        .filter(notEmpty) || []
    )
  }, [product.configurations])

  const selectConfiguration = useCallback((variant: UIVariant) => {
    setSelectedConfigurations((prevState) => {
      const { byProductId, byProductIdFilter, byVariantGroupId, byVariantGroupIdFilter } =
        variantHelpers(variant)
      const isConfigurationSelected = prevState.find(byProductId)
      const isVariantGroupSelected = prevState.find(byVariantGroupId)

      if (isConfigurationSelected) {
        return prevState.filter(byProductIdFilter)
      }

      if (isVariantGroupSelected) {
        return [...prevState.filter(byVariantGroupIdFilter), variant]
      }
      return [...prevState, variant]
    })
  }, [])

  const deselectAllFromConfigGroup = useCallback((variantGroupId: string) => {
    setSelectedConfigurations((prevState) => {
      const isVariantGroupSelected = prevState.find((v) => v.variantGroupId === variantGroupId)

      if (isVariantGroupSelected) {
        return [...prevState.filter((v) => v.variantGroupId !== variantGroupId)]
      }
      return prevState
    })
  }, [])

  const selectConfigurationOnUpdate = useCallback(
    (variantGroupId: string, variants: Option<UIVariant[]>) => {
      const isGroupOptional = !requiredGroupsIds.includes(variantGroupId)

      if (!variants) return

      if (!variants.length) {
        deselectAllFromConfigGroup(variantGroupId)
        return
      }

      const isSelectionPresent = variants.some((v) =>
        selectedConfigurations.map((_v) => _v.id).includes(v.id)
      )

      if (!isSelectionPresent && !isGroupOptional) {
        let variant = variants[0]
        const mainVariant = variants.find((e) => e.isMain)

        if (mainVariant) {
          variant = mainVariant
        }

        selectConfiguration(variant)
      }
    },
    [deselectAllFromConfigGroup, requiredGroupsIds, selectConfiguration, selectedConfigurations]
  )

  const dynamicPrice = useMemo<ProductPrice>(() => {
    const price = selectedConfigurations.reduce((total, obj) => total + getProductPrice(obj), 0)
    const originalPrice = selectedConfigurations.reduce(
      (total, obj) => getProductOriginalPrice(obj) + total,
      0
    )

    return {
      price,
      originalPrice
    }
  }, [selectedConfigurations])

  useEffect(() => {
    dispatchPrices({ type: 'set', payload: dynamicPrice })
  }, [dynamicPrice])

  const isConfigurationSelected = useCallback(
    (variant: UIVariant) => {
      return Boolean(
        selectedConfigurations.map((s) => s.id).find((productId) => productId === variant.id)
      )
    },
    [selectedConfigurations]
  )

  const isConfigurationBuyable = useMemo(() => {
    if (requiredGroupsIds.some((id) => !!variantsByIds?.[id]?.error)) {
      return false
    }

    const variantGroupWithSelection = selectedConfigurations
      .map((i) => i.variantGroupId)
      .filter(notEmpty)
    return requiredGroupsIds.some((e) => variantGroupWithSelection.includes(e))
  }, [requiredGroupsIds, selectedConfigurations, variantsByIds])

  return {
    prices,
    selectedConfigurations,
    selectConfiguration,
    isConfigurationSelected,
    selectConfigurationOnUpdate,
    isConfigurationBuyable
  }
}

export default useConfigurationVariants

const variantHelpers = (variant: UIVariant) => {
  return {
    byVariantGroupId: (e: UIVariant) => e.variantGroupId === variant.variantGroupId,
    byVariantGroupIdFilter: (e: UIVariant) => e.variantGroupId !== variant.variantGroupId,
    byProductId: (e: UIVariant) => e.id === variant.id,
    byProductIdFilter: (e: UIVariant) => e.id !== variant.id
  }
}
